import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import SEO from './components/SEO';
import NotFoundPage from './pages/NotFoundPage';

// Eagerly loaded: only the first public paint and lightweight providers.
import AppShellSkeleton from './components/AppShellSkeleton';
import { RealtimeProvider } from './components/RealtimeProvider';
import { ConfirmProvider } from './components/ConfirmDialog';
import { VendorCatalogProvider } from './contexts/VendorCatalogContext';
import { setTenantStorageContext, purgeTenantStorage } from './lib/tenantStorage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
// Iter 260.mk — Public marketing pages (3-route rework).
// Iter 260.mk.c — /work removed as standalone page; Recent Work now lives
// on the homepage. Route redirects to /#work below.
import ImpersonationBanner from './components/ImpersonationBanner';

// Eager: every impersonation-redirect target + the most-used tenant pages.
// These pages must NEVER show a "Loading…" spinner that could hang during
// the SA "Open as user" tab-switch flow (the original "FOREVER to load"
// bug from iter 145). Anything reachable in the first 1-2 clicks after
// impersonation lands stays here.

// ── Lazy with retry-on-fail. Iter 151 brings these back to lazy() for
// bundle-size reclaim — but with a 1-retry wrapper so a transient chunk
// fetch failure (network blip, deployment mid-flight) never strands the
// user on an infinite "Loading…" spinner like before. After the second
// failure we hard-reload to bypass any stale service worker / cache.
const lazyWithRetry = (factory) => lazy(async () => {
  try {
    return await factory();
  } catch (err) {
    // Single retry — covers most transient network blips.
    await new Promise((r) => setTimeout(r, 400));
    try {
      return await factory();
    } catch (err2) {
      // Hard refresh as the absolute last resort. Better than a stuck
      // spinner and rare enough that the UX hit is acceptable.
      if (typeof window !== 'undefined') window.location.reload();
      throw err2;
    }
  }
});

const LazyToaster = lazyWithRetry(() => import('./components/ui/sonner').then((m) => ({ default: m.Toaster })));
const showErrorToast = (message, options) => import('sonner').then(({ toast }) => toast.error(message, options)).catch(() => {});

function DeferredToaster({ defer }) {
  const [show, setShow] = useState(!defer);
  useEffect(() => {
    if (!defer || show) return undefined;
    let timer;
    let idleId;
    const reveal = () => setShow(true);
    if (window.requestIdleCallback) idleId = window.requestIdleCallback(reveal, { timeout: 3000 });
    else timer = window.setTimeout(reveal, 2200);
    return () => {
      if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timer) window.clearTimeout(timer);
    };
  }, [defer, show]);
  if (!show) return null;
  return <Suspense fallback={null}><LazyToaster richColors position="top-right" /></Suspense>;
}

// Route-level chunks keep operational dashboards, scanners, document tools,
// and markdown/payment libraries out of the public marketing first paint.
const DashboardLayout = lazyWithRetry(() => import('./components/DashboardLayout'));
const AdminPortalPreviewShell = lazyWithRetry(() => import('./components/layouts/AdminPortalPreviewShell'));
const SignupTrialPage = lazyWithRetry(() => import('./pages/SignupTrialPage'));
const SignupSuccessPage = lazyWithRetry(() => import('./pages/SignupTrialPage').then((m) => ({ default: m.SignupSuccessPage })));
const BillingSettingsPage = lazyWithRetry(() => import('./pages/BillingSettingsPage'));
const ContactUsPage = lazyWithRetry(() => import('./pages/ContactUsPage'));
const EventProductionPage = lazyWithRetry(() => import('./pages/EventProductionPage'));
const OperationsConsultingPage = lazyWithRetry(() => import('./pages/OperationsConsultingPage'));
const SoftwarePlatformPage = lazyWithRetry(() => import('./pages/SoftwarePlatformPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));
const BlogPage = lazyWithRetry(() => import('./pages/BlogPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./pages/LegalPage').then((m) => ({ default: m.TermsOfServicePage })));
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/LegalPage').then((m) => ({ default: m.PrivacyPolicyPage })));
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const PortalMagicRedeemPage = lazyWithRetry(() => import('./pages/PortalMagicRedeemPage'));
const ForcePasswordChangePage = lazyWithRetry(() => import('./pages/ForcePasswordChangePage'));
const OnboardingPage = lazyWithRetry(() => import('./pages/OnboardingPage'));
const ImpersonateRedeemPage = lazyWithRetry(() => import('./pages/ImpersonateRedeemPage'));
const LogoutPage = lazyWithRetry(() => import('./pages/LogoutPage'));
const BlogPostPage = lazyWithRetry(() => import('./pages/BlogPostPage'));
const KESSignPage = lazyWithRetry(() => import('./pages/KESSignPage'));
const KESContractPrintPage = lazyWithRetry(() => import('./pages/KESContractPrintPage'));
const KESContractsPage = lazyWithRetry(() => import('./pages/KESContractsPage'));
const KESInvoicesPage = lazyWithRetry(() => import('./pages/KESInvoicesPage'));
const KESInvoicePublicPage = lazyWithRetry(() => import('./pages/KESInvoicePublicPage'));
const KESInvoicePrintPage = lazyWithRetry(() => import('./pages/KESInvoicePrintPage'));
const KESBlogPage = lazyWithRetry(() => import('./pages/KESBlogPage'));
const MyFinancesPage = lazyWithRetry(() => import('./pages/MyFinancesPage'));
const PublicVendorDirectoryPage = lazyWithRetry(() => import('./pages/PublicVendorDirectoryPage'));
const CheckInScanPage = lazyWithRetry(() => import('./pages/CheckInScanPage'));
const TicketScanPage = lazyWithRetry(() => import('./pages/TicketScanPage'));
const TicketScanDashboard = lazyWithRetry(() => import('./pages/TicketScanDashboard'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const EventsPage = lazyWithRetry(() => import('./pages/EventsPage'));
const EventDetailPage = lazyWithRetry(() => import('./pages/EventDetailPage'));
const LeadsPage = lazyWithRetry(() => import('./pages/LeadsPage'));
const CalendarPage = lazyWithRetry(() => import('./pages/CalendarPage'));
const ClientsPage = lazyWithRetry(() => import('./pages/ClientsPage'));
const InboxPage = lazyWithRetry(() => import('./pages/InboxPage'));
const SuperAdminPage = lazyWithRetry(() => import('./pages/SuperAdminPage'));
const MarketingUploadsPage = lazyWithRetry(() => import('./pages/MarketingUploadsPage'));

const VendorsPage = lazyWithRetry(() => import('./pages/VendorsPage'));
const StaffPage = lazyWithRetry(() => import('./pages/StaffPage'));
const InvoicesPage = lazyWithRetry(() => import('./pages/InvoicesPage'));
const ReportingPage = lazyWithRetry(() => import('./pages/ReportingPage'));
const AIAssistantPage = lazyWithRetry(() => import('./pages/AIAssistantPage'));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'));
const HelpPage = lazyWithRetry(() => import('./pages/HelpPage'));
const ClientPortalPage = lazyWithRetry(() => import('./pages/ClientPortalPage'));
const VendorPortalPage = lazyWithRetry(() => import('./pages/VendorPortalPage'));
const VendorPerformancePage = lazyWithRetry(() => import('./pages/VendorPerformancePage'));
const VenuePortalPage = lazyWithRetry(() => import('./pages/VenuePortalPage'));
const StaffSchedulePage = lazyWithRetry(() => import('./pages/StaffSchedulePage'));
const StaffPortalPage = lazyWithRetry(() => import('./pages/StaffPortalPage'));
const AutomationsPage = lazyWithRetry(() => import('./pages/AutomationsPage'));
const SharedPortalPage = lazyWithRetry(() => import('./pages/SharedPortalPage'));
const VolunteerApplyPage = lazyWithRetry(() => import('./pages/VolunteerApplyPage'));
const VenuesManagementPage = lazyWithRetry(() => import('./pages/VenuesPage'));
const PermissionsPage = lazyWithRetry(() => import('./pages/PermissionsPage'));
const InviteAcceptPage = lazyWithRetry(() => import('./pages/InviteAcceptPage'));
const PortalPreviewPage = lazyWithRetry(() => import('./pages/PortalPreviewPage'));
const WhiteLabelPortalPage = lazyWithRetry(() => import('./pages/WhiteLabelPortalPage'));
const RecycleBinPage = lazyWithRetry(() => import('./pages/RecycleBinPage'));
const PublicApplicationPage = lazyWithRetry(() => import('./pages/PublicApplicationPage'));
const PublicTicketsPage = lazyWithRetry(() => import('./pages/PublicTicketsPage'));
const PublicTicketsSuccessPage = lazyWithRetry(() => import('./pages/PublicTicketsSuccessPage'));
const PublicOrgEventsPage = lazyWithRetry(() => import('./pages/PublicOrgEventsPage'));
const VendorBoothFeePage = lazyWithRetry(() => import('./pages/VendorBoothFeePage'));
const VendorInvoicePayPage = lazyWithRetry(() => import('./pages/VendorInvoicePayPage'));
const PaymentSuccessPage = lazyWithRetry(() => import('./pages/PaymentSuccessPage'));

// Background route-chunk prefetch. After bootstrap completes and the
// dashboard paints, eagerly download the chunks for secondary nav items
// the user is likely to visit next. Without this, the first click on
// Settings / Reports / Invoices / etc. blocks on a fresh chunk fetch
// (~100-500ms), surfaced as "Loading…" in the Suspense fallback. With
// this, those clicks become instantaneous because the chunk is already
// in the browser's module cache.
//
// Scheduled via requestIdleCallback so we never compete with the
// dashboard's first-paint work. Falls back to setTimeout(2s) on browsers
// without the idle callback API. We split into two waves so the very
// first batch lands quickly, then the rarer pages trickle in later.
const _prefetchedRoutes = { done: false };
const prefetchSecondaryRoutes = (role) => {
  if (_prefetchedRoutes.done) return;
  _prefetchedRoutes.done = true;
  const isInternal = role && !['client', 'vendor', 'staff', 'contractor'].includes(role);
  const isPortal = role && ['client', 'vendor', 'staff', 'contractor'].includes(role);
  const sched = (cb, delay = 0) => {
    if (typeof window === 'undefined') return;
    if (window.requestIdleCallback) {
      window.requestIdleCallback(cb, { timeout: 4000 + delay });
    } else {
      setTimeout(cb, 1500 + delay);
    }
  };
  // Internal: most common secondary destinations first.
  if (isInternal) {
    sched(() => {
      import('./pages/InvoicesPage');
      import('./pages/SettingsPage');
      import('./pages/StaffPage');
    });
    sched(() => {
      import('./pages/ReportingPage');
      import('./pages/AIAssistantPage');
      import('./pages/AutomationsPage');
      import('./pages/VendorsPage');
    }, 800);
    sched(() => {
      import('./pages/VenuesPage');
      import('./pages/PermissionsPage');
      import('./pages/StaffSchedulePage');
      import('./pages/HelpPage');
    }, 1600);
  }
  // Portal users only need their own portal page chunks.
  if (isPortal) {
    sched(() => {
      import('./pages/ClientPortalPage');
      import('./pages/VendorPortalPage');
      import('./pages/StaffPortalPage');
    });
  }
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Per-tab impersonation: when the Super Admin opens "View as user", the
// new tab stashes a 1-hour Bearer token in sessionStorage. axios prefers
// that token over the shared cookie session — so the new tab runs under
// the impersonated identity while the original tab keeps the SA cookie
// intact. SessionStorage is per-tab in browsers, which is exactly the
// isolation we need to support multi-role testing in one browser profile.
export const IMPERSONATION_KEY = 'kewl_impersonation';

export const getImpersonation = () => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const clearImpersonation = () => {
  try { sessionStorage.removeItem(IMPERSONATION_KEY); } catch {}
};

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Inject the impersonation Bearer token if this tab is in "View as" mode.
api.interceptors.request.use((config) => {
  const imp = getImpersonation();
  if (imp?.token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${imp.token}`;
    // When the cookie also exists (the SA's own session), `withCredentials`
    // would still send it. Bearer wins on the backend (header is checked
    // before cookie in get_current_user), but disabling cookie inclusion
    // for this tab keeps the audit trail clean.
    config.withCredentials = false;
  }
  return config;
});

// Automatic token refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => error ? prom.reject(error) : prom.resolve());
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    // Iter 220.99.80 — Global mutation broadcast. User reported every
    // save in the workspace requires a full page refresh to see changes.
    // Root cause: each child component has its own local-cache pattern
    // and there's no cross-component invalidation. Rather than refactor
    // every child to call onRefresh after every mutation, we publish a
    // single `kewl:data-changed` window event after any successful
    // POST/PUT/PATCH/DELETE against /api/. Listeners (EventDetailPage,
    // VendorsPage, EventsPage, etc.) refetch on receipt.
    //
    // Skip GETs (no mutation), auth refresh (loop guard), and a few
    // realtime/poll endpoints to avoid feedback storms.
    try {
      const cfg = response.config || {};
      const method = (cfg.method || '').toUpperCase();
      const url = cfg.url || '';
      const SKIP = ['/auth/', '/realtime/', '/inbox/stream', '/correspondence-stream', '/chat/messages/', '/notifications/poll'];
      const isMutation = method && method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
      const isSkip = SKIP.some((s) => url.includes(s));
      if (isMutation && !isSkip) {
        window.dispatchEvent(new CustomEvent('kewl:data-changed', {
          detail: { method, url, status: response.status },
        }));
      }
    } catch { /* never block the response on the broadcast */ }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // Iter 220.17 — surface 402 (plan_limit_reached) as a global toast so
    // the user sees a consistent "Upgrade your plan" message regardless of
    // which call site triggered it. Call sites can still consume
    // `error.response.data.detail` for inline UI when they want to.
    if (error.response?.status === 402) {
      const d = error.response?.data?.detail;
      const msg = (d && typeof d === 'object' && d.message)
        || (typeof d === 'string' ? d : 'You have reached your plan limit. Upgrade to add more.');
      // Capture structured payload so the upgrade dialog can highlight the
      // tier that lifts the cap the user just hit.
      const detail = (d && typeof d === 'object' && d.error === 'plan_limit_reached')
        ? { resource: d.resource, limit: d.limit, current: d.current }
        : null;
      showErrorToast(msg, {
        duration: 8000,
        action: detail ? {
          label: 'Upgrade plan',
          onClick: () => window.dispatchEvent(new CustomEvent('kewl:open-upgrade', { detail })),
        } : undefined,
      });
    }
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve: () => resolve(api(originalRequest)), reject });
        });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // ZZ15n (Feb 2026): when the refresh cookie is missing/expired the
        // backend returns 401 with "No refresh token". That used to bubble
        // up as a confusing toast on every edit/save in the SA panel. Now
        // we redirect to /login with a friendly message and a `next` param
        // so the user lands back where they were after re-auth.
        const detail = refreshError?.response?.data?.detail || '';
        const isExpired = refreshError?.response?.status === 401
          || /no refresh token|invalid refresh token|expired/i.test(String(detail));
        if (isExpired && typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          showErrorToast('Your session expired — please log in again.');
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?next=${next}`;
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export const formatApiErrorDetail = (detail) => {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || d).join(', ');
  // Iter 220.17 — usage_guard 402 returns { error, resource, limit, current, message, ... }.
  // Surface the human-readable message rather than a JSON blob.
  if (detail.message) return detail.message;
  return JSON.stringify(detail);
};

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const OrgContext = createContext(null);
export const useOrg = () => useContext(OrgContext);

// Determine the default landing page based on user role
const getDefaultRoute = (userObj) => {
  if (!userObj) return '/';
  // Force first-time users to set their own password before accessing anything.
  if (userObj.must_change_password) return '/change-password';
  const role = userObj.role;
  if (role === 'super_admin') return '/super-admin';
  if (role === 'client') return '/my-portal';
  if (role === 'vendor') return '/my-portal';
  if (role === 'staff') return '/my-portal';
  return '/dashboard';
};

// Portal-only roles cannot access the internal CRM
const PORTAL_ROLES = ['staff', 'vendor', 'client'];
const isPortalRole = (user) => user && PORTAL_ROLES.includes(user.role);

// Internal route guard: redirects portal-only roles to /my-portal, AND
// redirects Super Admins to /super-admin so they never land on the tenant
// event-management dashboard. The Super Admin is the platform owner — they
// don't run their own events on the platform; they oversee everyone else's.
const InternalRoute = ({ children }) => {
  const { user } = useAuth();
  if (isPortalRole(user)) return <Navigate to="/my-portal" />;
  // Super Admins are allowed to browse the operator view. They can jump
  // back via the "Switch to Super Admin" button in the sidebar.
  return children;
};

// Route-level permission guard: redirects to /dashboard if user lacks the required permission.
// Reads from cached AuthContext.permissions so navigating between routes never refetches.
const PermGuard = ({ perm, children }) => {
  const { user, permissions, permissionsLoaded } = useAuth();
  if (!permissionsLoaded) return null; // loading (first boot only)
  if (user?.role === 'admin') return children;
  if (perm === 'settings' && user?.role !== 'admin') return <Navigate to="/dashboard" />;
  if (!permissions[perm]) return <Navigate to="/dashboard" />;
  return children;
};

// Auth callback for Google OAuth — processes #session_id= fragment
const AuthCallback = ({ setUser, setOrgSettings }) => {
  const hasProcessed = React.useRef(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { navigate('/login'); return; }

    const sessionId = match[1];
    // Clear the hash
    window.history.replaceState(null, '', window.location.pathname);

    api.post('/auth/google/session', { session_id: sessionId })
      .then(async (res) => {
        setUser(res.data);
        try { const r = await api.get('/auth/org-settings'); setOrgSettings(r.data); } catch {}
        navigate(getDefaultRoute(res.data));
      })
      .catch(() => navigate('/login'));
  }, [navigate, setUser, setOrgSettings]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#FDFBF7]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A3C31] mx-auto mb-4"></div>
        <p className="text-sm text-[#A89685]">Signing in with Google...</p>
      </div>
    </div>
  );
};

const PUBLIC_FIRST_PAINT_PATHS = ['/', '/production', '/consulting', '/software', '/about', '/contact', '/blog', '/terms', '/privacy'];
const isPublicFirstPaintPath = (pathname) => PUBLIC_FIRST_PAINT_PATHS.some((path) => pathname === path || (path === '/blog' && pathname.startsWith('/blog/')));

function App() {
  const fastPublicPaint = isPublicFirstPaintPath(window.location.pathname)
    && localStorage.getItem('kewl.authenticated') !== '1';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(!fastPublicPaint);
  const [orgSettings, setOrgSettings] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Iter 257.n — Keep the tenantStorage helper in lock-step with the
  // current auth identity so any code using `tStorage.local/.session`
  // namespaces under the right user/org without needing to be aware
  // of the provider tree.
  useEffect(() => {
    setTenantStorageContext(user);
  }, [user?.user_id, user?.org_id]);

  useEffect(() => {
    // Initialize theme from localStorage
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') document.documentElement.classList.add('dark');

    // CRITICAL: If returning from OAuth callback, skip the bootstrap.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Single-shot bootstrap: collapses /auth/me + /auth/permissions +
    // /auth/org-settings into one round-trip. Saves ~2× RTT on cold load
    // (was a 3-call waterfall, ~300ms before this; now ~100ms warm). The
    // server caches both org-settings and permission resolution for 60s.
    const bootstrap = async () => {
      try {
        const res = await api.get('/auth/bootstrap');
        const { user: u, permissions: perm, org_settings: org, dashboard, inbox_counts, notifications_unread, subscription } = res.data || {};
        setUser(u || null);
        if (u) localStorage.setItem('kewl.authenticated', '1');
        else localStorage.removeItem('kewl.authenticated');
        // Iter 220.99.99zz9W — Stash the effective timezone so every
        // datetime helper (`formatInUserTz`) reads it synchronously.
        // Then opportunistically sync the BROWSER tz to the backend
        // for new users who haven't picked one yet — captures the
        // sensible default without forcing a settings-page detour.
        if (u && u.effective_timezone) {
          try {
            const { persistEffectiveTimezone, syncBrowserTimezoneToBackend } = await import('./utils/datetime');
            persistEffectiveTimezone(u.effective_timezone);
            // Fire-and-forget — never block paint.
            syncBrowserTimezoneToBackend(api, u.timezone || u.effective_timezone);
          } catch { /* helper not present in this build */ }
        }
        if (perm) {
          setPermissions(perm.permissions || {});
          setPermissionsLoaded(true);
        } else if (!u) {
          // Iter 220.99.99zzf-2 — Bootstrap returns 200 with user=null for
          // unauthenticated callers (no 401 noise). Make sure permissions
          // are marked "loaded" so the auth-gated routes don't hang on
          // permissionsLoaded=false forever waiting for a perm payload
          // that's never coming.
          setPermissions({});
          setPermissionsLoaded(true);
        }
        if (org) setOrgSettings(org);
        // Hand off the prefetched dashboard rollup to DashboardPage so it
        // paints with real numbers on first render — no extra round-trip
        // on cold load. DashboardPage clears this after first read so it
        // doesn't survive across navigations / reloads.
        if (dashboard) window.__bootstrapDashboard = dashboard;
        // Same trick for the sidebar inbox badge + bell notification badge:
        // these previously flickered through "0" before /inbox/unread-count
        // and /notifications/unread-count returned. Now they paint with
        // the right number on first render.
        if (inbox_counts) window.__bootstrapInboxCounts = inbox_counts;
        if (typeof notifications_unread === 'number') window.__bootstrapNotifsUnread = notifications_unread;
        if (subscription) window.__bootstrapSubscription = subscription;
        // Kick off background download of secondary route chunks (Settings,
        // Reports, Invoices, etc.) so first-click on those routes is
        // instant instead of stalling on a fresh chunk fetch.
        if (u?.role) prefetchSecondaryRoutes(u.role);
      } catch {
        setUser(null);
        setPermissions({});
        setPermissionsLoaded(true);
      } finally {
        setLoading(false);
      }
    };
    if (fastPublicPaint) {
      // Prospective visitors should see the marketing page immediately.
      // Session discovery still runs shortly afterward without competing
      // with critical CSS, fonts, and the hero image.
      let timer;
      let idleId;
      if (window.requestIdleCallback) idleId = window.requestIdleCallback(bootstrap, { timeout: 1200 });
      else timer = window.setTimeout(bootstrap, 600);
      return () => {
        if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
        if (timer) window.clearTimeout(timer);
      };
    }
    bootstrap();
    return undefined;
  }, [fastPublicPaint]);

  // Backstop: if the user object lands via login (not bootstrap), fetch
  // permissions for it. Bootstrap handles the cold-load case; this handles
  // the post-login transition where setUser fires before permissions exist.
  useEffect(() => {
    if (!user) { setPermissions({}); setPermissionsLoaded(false); return; }
    if (permissionsLoaded) return;
    let cancelled = false;
    api.get('/auth/permissions')
      .then((r) => { if (!cancelled) { setPermissions(r.data?.permissions || {}); setPermissionsLoaded(true); } })
      .catch(() => { if (!cancelled) { setPermissions({}); setPermissionsLoaded(true); } });
    return () => { cancelled = true; };
  }, [user?.user_id, permissionsLoaded]);

  const refreshOrgSettings = async () => {
    try { const r = await api.get('/auth/org-settings'); setOrgSettings(r.data); } catch {}
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    localStorage.setItem('kewl.authenticated', '1');
    setUser(response.data);
    try { const r = await api.get('/auth/org-settings'); setOrgSettings(r.data); } catch {}
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await api.post('/auth/register', { name, email, password });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    // Call the backend FIRST so the cookie-clear Set-Cookie response is
    // received and applied while React state is still settled. If we set
    // the user to null first, the route guard immediately unmounts the
    // logged-in tree, and although the in-flight fetch usually completes,
    // we've seen browsers race the unmount in production and end up with
    // dangling cookies. Server first, state second is the safe order.
    try {
      await api.post('/auth/logout');
    } catch {
      try { await api.post('/auth/force-logout-all'); } catch {}
    }
    clearImpersonation();
    // Iter 257.m — Purge per-tenant caches so the next user on this
    // tab can't see leftover data from this session. Sweep matches
    // every cache key prefix any tenant-scoped feature has written.
    // Iter 257.n — Also sweeps everything written through the new
    // `tStorage` helper (`tnt.u.*`, `tnt.o.*` prefixes) so future
    // tenant-scoped caches don't need their prefixes hard-coded here.
    try {
      const TENANT_CACHE_PREFIXES = ['kewl.cal.', 'kewl.dash.', 'kewl.events.', 'kewl.pipeline.'];
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && TENANT_CACHE_PREFIXES.some(p => k.startsWith(p))) keys.push(k);
      }
      keys.forEach(k => sessionStorage.removeItem(k));
    } catch { /* sessionStorage unavailable */ }
    purgeTenantStorage();
    localStorage.removeItem('kewl.authenticated');
    setUser(null);
  };

  if (loading) {
    // Layout-aware shell instead of a blank page with a tiny spinner.
    // Shows the brand + nav silhouette + content skeletons immediately so
    // cold-load doesn't feel like a broken page. Real content paints over
    // this skeleton as soon as bootstrap resolves (~150-300ms warm).
    return <AppShellSkeleton />;
  }

  return (
    <HelmetProvider>
    <AuthContext.Provider value={{ user, login, register, logout, setUser, permissions, permissionsLoaded }}>
    <OrgContext.Provider value={{ orgSettings, refreshOrgSettings }}>
      <RealtimeProvider user={user} apiBaseUrl={`${API_URL}/api`}>
      <VendorCatalogProvider authed={!!user}>
      <ConfirmProvider>
      <BrowserRouter>
        <ImpersonationBanner />
        <AppRoutes user={user} setUser={setUser} setOrgSettings={setOrgSettings} />
      </BrowserRouter>
      </ConfirmProvider>
      </VendorCatalogProvider>
      </RealtimeProvider>
      <DeferredToaster defer={fastPublicPaint} />
    </OrgContext.Provider>
    </AuthContext.Provider>
    </HelmetProvider>
  );
}

// Separate component inside BrowserRouter so useLocation works
function AppRoutes({ user, setUser, setOrgSettings }) {
  const location = useLocation();
  const { orgSettings } = useOrg();

  // CRITICAL: Detect session_id synchronously during render (before routes)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} setOrgSettings={setOrgSettings} />;
  }
  // First-login lockout: if the Super Admin provisioned this account, force
  // the user to set their own password before accessing any protected route.
  // Whitelist /change-password, /login, and /logout so the user can always
  // escape (e.g. when the wrong account is logged in via a leftover cookie).
  // Also skipped for impersonation sessions.
  const isImpersonating = !!getImpersonation();
  const escapeRoutes = ['/change-password', '/login', '/logout', '/forgot-password', '/reset-password'];
  const onEscapeRoute = escapeRoutes.some(p => location.pathname.startsWith(p));
  if (user?.must_change_password && !onEscapeRoute && !isImpersonating) {
    return <Navigate to="/change-password" replace />;
  }

  // Iter 254.4 — Removed the gate that force-redirected first-time admin
  // users to /onboarding when `onboarding_completed === false`. Per user
  // direction (production): "When a user is in the platform for the first
  // time it should go straight to the Dashboard Page." The /onboarding
  // route still exists and can be entered manually from the user menu;
  // we just don't bounce admins there on first login anymore.
  // (The original iter-220.99.99zz9ZZ15s gate + `onboardingEscapes` allow-
  // list were both removed; nothing else referenced them.)

  return (
    <>
      {/* Iter 260 — Per-route SEO metadata driven by location.pathname.
          Public marketing routes get a unique title/description/canonical.
          All other (authenticated / portal / operational) routes fall
          through to a `noindex, nofollow, noarchive` default so search
          engines and archive services never index them. The homepage's
          JSON-LD @graph lives in `public/index.html`, so this component
          never emits application/ld+json — preventing duplicate scripts
          across React navigation. */}
      <RouteSEO pathname={location.pathname} />
      {/* Suspense boundary for lazy-loaded chunks (iter 151).
          Fallback is a minimal centered skeleton — kept short so a successful
          chunk fetch swaps in within ~100-200ms without flicker. The
          lazyWithRetry wrapper guarantees we never strand the user here:
          a chunk failure retries once, then hard-reloads. */}
      <Suspense fallback={
        <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7]" data-testid="route-loading">
          <div className="flex items-center gap-2 text-[#A89685] text-xs">
            <div className="w-3.5 h-3.5 border-2 border-[#A89685] border-t-transparent rounded-full animate-spin" />
            Loading…
          </div>
        </div>
      }>
      <Routes>
          {/* Public Routes */}
          <Route path="/" element={!user ? <LandingPage /> : <Navigate to={getDefaultRoute(user)} />} />
          {/* Login route: allow access even when a stale `must_change_password` user
              is in the cookie — gives the user a reliable way to switch accounts.
              `?fresh=1` (used by welcome / reset emails) ALSO bypasses auto-redirect
              so a logged-in cookie can never silently land on the wrong dashboard. */}
          <Route path="/login" element={
            (!user || user.must_change_password || new URLSearchParams(location.search).get('fresh') === '1')
              ? <LoginPage />
              : <Navigate to={getDefaultRoute(user)} />
          } />
          {/* Self-service signup disabled — accounts are provisioned by the Super Admin. Redirect legacy URLs to the demo/contact form. */}
          <Route path="/signup" element={<SignupTrialPage />} />
          <Route path="/signup/plans" element={<SignupTrialPage />} />
          <Route path="/signup/success" element={<SignupSuccessPage />} />
          <Route path="/register" element={<Navigate to="/signup" replace />} />
          <Route path="/settings/billing" element={user ? <BillingSettingsPage /> : <Navigate to="/login" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          {/* Iter 260.mk — 3-route public marketing hubs + support pages.
              Note: `/events` is taken by the authenticated CRM route, so
              the public Events hub sits at `/production`. Nav labels use
              friendlier names ("Events") independent of URL. */}
          <Route path="/production" element={!user ? <EventProductionPage /> : <Navigate to={getDefaultRoute(user)} />} />
          <Route path="/consulting" element={!user ? <OperationsConsultingPage /> : <Navigate to={getDefaultRoute(user)} />} />
          <Route path="/software" element={!user ? <SoftwarePlatformPage /> : <Navigate to={getDefaultRoute(user)} />} />
          <Route path="/about" element={!user ? <AboutPage /> : <Navigate to={getDefaultRoute(user)} />} />
          {/* Iter 260.mk.c — /work redirects to homepage anchor per user direction. */}
          <Route path="/work" element={<Navigate to="/#work" replace />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          {/* Kewl Event Solutions private contract signing (no auth, token-based) */}
          <Route path="/sign/:token" element={<KESSignPage />} />
          <Route path="/kes-contract-print/:contractId" element={<KESContractPrintPage />} />
          <Route path="/invoice/:token" element={<KESInvoicePublicPage />} />
          <Route path="/kes-invoice-print/:invoiceId" element={<KESInvoicePrintPage />} />
          {/* Kewl Event Solutions private contracts admin (Super Admin only) */}
          <Route path="/super-admin/kes-contracts" element={user?.role === 'super_admin' ? <KESContractsPage /> : <Navigate to="/login" />} />
          <Route path="/super-admin/kes-contracts/:contractId" element={user?.role === 'super_admin' ? <KESContractsPage /> : <Navigate to="/login" />} />
          <Route path="/super-admin/kes-invoices" element={user?.role === 'super_admin' ? <KESInvoicesPage /> : <Navigate to="/login" />} />
          <Route path="/super-admin/kes-invoices/:invoiceId" element={user?.role === 'super_admin' ? <KESInvoicesPage /> : <Navigate to="/login" />} />
          <Route path="/super-admin/blog" element={user?.role === 'super_admin' ? <KESBlogPage /> : <Navigate to="/login" />} />
          <Route path="/super-admin/my-finances" element={user?.role === 'super_admin' ? <MyFinancesPage /> : <Navigate to="/login" />} />
          {/* Public Insights blog (indexable) */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          {/* Iter 220.91 — Public Vendor Directory (no auth, branded) */}
          <Route path="/event/:eventId/vendors" element={<PublicVendorDirectoryPage />} />
          {/* Iter 220.99.60 — Public ticket landing page + success */}
          <Route path="/event/:eventId/tickets" element={<PublicTicketsPage />} />
          <Route path="/event/:eventId/tickets/success" element={<PublicTicketsSuccessPage />} />
          {/* Iter 220.99.99h — public discovery page (org slug or org_id). */}
          <Route path="/org/:orgIdentifier/events" element={<PublicOrgEventsPage />} />
          {/* Iter 220.91 — Day-of check-in QR scan landing */}
          <Route path="/check-in/:token" element={<CheckInScanPage />} />
          {/* Iter 220.99.99zz32 — Ticket check-in surfaces.
              Iter 220.99.99zz40 — /ticket-scan removed from InternalRoute
              so gate staff (role=staff) can reach the page from their
              phone after logging in. The backend check-in endpoint
              already enforces operator-or-staff at the request layer,
              so the frontend wrapper was double-gating and bouncing
              legit staff to /my-portal. Added a backward-compat alias
              `/scan/ticket/...` for any legacy PDFs already in the
              wild that encode the old path. */}
          <Route path="/ticket-scan/:eventId/:ticketCode" element={<TicketScanPage />} />
          <Route path="/scan/ticket/:eventId/:ticketCode" element={<TicketScanPage />} />
          <Route path="/events/:eventId/scan" element={<InternalRoute><TicketScanDashboard /></InternalRoute>} />
          <Route path="/accept-invite/:token" element={<InviteAcceptPage />} />
          {/* Passwordless magic-link redeem — lands here from emailed CTAs */}
          <Route path="/portal/magic/:token" element={<PortalMagicRedeemPage />} />
          <Route path="/p/:slug" element={<WhiteLabelPortalPage />} />
          <Route path="/apply/:orgSlug/:eventSlug" element={<PublicApplicationPage />} />
          <Route path="/pay/booth/:applicationId" element={<VendorBoothFeePage />} />
          {/* Iter 220.70 — public vendor invoice landing page (clicked from
              the "Pay $X" CTA in vendor invoice emails). */}
          <Route path="/pay/vendor-invoice/:invoiceId" element={<VendorInvoicePayPage />} />
          {/* Iter 220.99.99s — Unified Stripe Checkout success landing.
              Used by client invoices (?invoice_id=...) AND vendor booth
              fees (?application_id=...). Previously this route didn't
              exist and paid clients/vendors landed on a 404 even though
              their card had been charged successfully. */}
          <Route path="/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/onboarding" element={user ? <OnboardingPage /> : <Navigate to="/login" />} />
          {/* Force password change when must_change_password flag is set (first-login flow). */}
          <Route path="/change-password" element={user ? <ForcePasswordChangePage /> : <Navigate to="/login" />} />
          {/* Universal logout escape — always clears cookies + impersonation. */}
          <Route path="/logout" element={<LogoutPage />} />
          {/* Super Admin "Open as user" redeem — opens in a new tab and stashes the
              token in sessionStorage so this tab runs with a different identity than
              the original SA tab. */}
          <Route path="/impersonate/redeem" element={<ImpersonateRedeemPage />} />

          {/* Role-specific Portal Routes (standalone, no admin sidebar) */}
          <Route path="/my-portal" element={user ? (
            user.role === 'client' ? <ClientPortalPage /> :
            user.role === 'vendor' ? <VendorPortalPage /> :
            <StaffPortalPage />
          ) : <Navigate to="/login" />} />

          {/* Iter 220.99.99zz24 — "View-as-Portal" routes for operators.
              Previously wrapped in DashboardLayout which kept the
              internal sidebar visible (confusing). Now wrapped in a
              slim preview shell that mirrors what the actual portal
              user sees, with a top banner so the operator knows they
              are in preview mode. */}
          <Route path="/portal/client/:clientId" element={user ? <InternalRoute><AdminPortalPreviewShell><ClientPortalPage /></AdminPortalPreviewShell></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/portal/vendor/:vendorId" element={user ? <InternalRoute><AdminPortalPreviewShell><VendorPortalPage /></AdminPortalPreviewShell></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/portal/staff/:staffId" element={user ? <InternalRoute><AdminPortalPreviewShell><StaffPortalPage /></AdminPortalPreviewShell></InternalRoute> : <Navigate to="/login" />} />

          {/* Iter 220.99.63 — Dedicated venue portal alias. Today it
              delegates to VendorPortalPage (which already detects
              is_venue_contact and adapts copy). The seam exists so
              future venue-only features land without touching the
              vendor file. */}
          <Route path="/venue-portal" element={user ? <VenuePortalPage /> : <Navigate to="/login" />} />
          <Route path="/portal/venue/:venueId" element={user ? <InternalRoute><AdminPortalPreviewShell><VenuePortalPage /></AdminPortalPreviewShell></InternalRoute> : <Navigate to="/login" />} />

          {/* Legacy bare /portal/{role} URLs — these were sent in older emails
              before we migrated to magic links. Without this fallback they'd
              fall through to "*" and dump the user on the marketing landing
              page. Now: portal users go to their own portal, internal users
              go to the dashboard, and unauthenticated users land on the
              redeem page in "request a fresh link" mode (token "expired"). */}
          <Route path="/portal/client" element={
            user ? (isPortalRole(user) ? <Navigate to="/my-portal" replace /> : <Navigate to="/dashboard" replace />)
                 : <Navigate to="/portal/magic/expired" replace />
          } />
          <Route path="/portal/vendor" element={
            user ? (isPortalRole(user) ? <Navigate to="/my-portal" replace /> : <Navigate to="/dashboard" replace />)
                 : <Navigate to="/portal/magic/expired" replace />
          } />
          <Route path="/portal/staff" element={
            user ? (isPortalRole(user) ? <Navigate to="/my-portal" replace /> : <Navigate to="/dashboard" replace />)
                 : <Navigate to="/portal/magic/expired" replace />
          } />

          {/* Protected Internal CRM Routes — portal roles (staff/vendor/client) are redirected to /my-portal */}
          <Route path="/dashboard" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="dashboard"><DashboardPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/events" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="events"><EventsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/events/recycle-bin" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="events"><RecycleBinPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/events/:id" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="events"><EventDetailPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/pipeline" element={user ? <Navigate to="/leads?tab=pipeline" replace /> : <Navigate to="/login" />} />
          <Route path="/inquiries" element={user ? <Navigate to="/leads?tab=inquiries" replace /> : <Navigate to="/login" />} />
          <Route path="/leads" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="crm"><LeadsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/calendar" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="calendar"><CalendarPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/clients" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="crm"><ClientsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/vendors" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="vendors"><VendorsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          {/* Iter 220.99.99zz20 — Cross-event vendor performance view */}
          <Route path="/vendors/:vendorId/performance" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="vendors"><VendorPerformancePage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/staff" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="staff"><StaffPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/invoices" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="invoices"><InvoicesPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/vendor-payments" element={<Navigate to="/reports?tab=vendor_payments" replace />} />
          <Route path="/reports" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="reports"><ReportingPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/ai-assistant" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="ai"><AIAssistantPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/inbox" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="inbox"><InboxPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/settings" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="settings"><SettingsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/help" element={user ? <InternalRoute><HelpPage /></InternalRoute> : <Navigate to="/login" />} />
          {/* Legacy redirects: /tasks, /companies, and /financials were removed as they duplicated
              existing views (tasks live inside event workspace; companies overlap with /clients;
              financial dashboard merged into /reports). Bookmarks land on the next-best destination. */}
          <Route path="/tasks" element={<Navigate to="/dashboard" replace />} />
          <Route path="/companies" element={<Navigate to="/clients" replace />} />
          <Route path="/financials" element={<Navigate to="/reports" replace />} />
          <Route path="/staff-schedule" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="staff"><StaffSchedulePage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/automations" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="settings"><AutomationsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/permissions" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="settings"><PermissionsPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/venues-db" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="events"><VenuesManagementPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />
          <Route path="/portal-preview" element={user ? <InternalRoute><DashboardLayout><PermGuard perm="settings"><PortalPreviewPage /></PermGuard></DashboardLayout></InternalRoute> : <Navigate to="/login" />} />

          {/* Super Admin (standalone, no sidebar) */}
          <Route path="/super-admin" element={user?.role === 'super_admin' ? <SuperAdminPage /> : <Navigate to="/dashboard" />} />
          <Route path="/super-admin/marketing-uploads" element={user?.role === 'super_admin' ? <MarketingUploadsPage /> : <Navigate to="/dashboard" />} />

          {/* Shared Portal (no auth required) */}
          <Route path="/shared/:token" element={<SharedPortalPage />} />
          <Route path="/volunteer/:eventId" element={<VolunteerApplyPage />} />

          {/* Fallback */}
          <Route path="/404" element={<NotFoundPage />} />
          <Route
            path="*"
            element={
              user
                ? <Navigate to={getDefaultRoute(user)} />
                : <NotFoundPage />
            }
          />
        </Routes>
      </Suspense>
    </>
  );
}

// Iter 260 — Route → SEO metadata mapping. Public routes get real titles
// and canonicals; everything else is force-noindexed. Keep in sync with
// robots.txt and sitemap.xml.
const PUBLIC_ROUTE_META = {
  '/': {
    title: null, // uses default homepage title
    description:
      'Production, consulting, and platform — one team behind all three. We produce events, consult with businesses and venues on their operations, and build the software that keeps every operation running.',
    canonicalPath: '/',
  },
  '/production': {
    title: 'Event Operations',
    description:
      'Full-service event operations for festivals, corporate activations, and private events. Phoenix-based operators with a national footprint.',
    canonicalPath: '/production',
  },
  '/consulting': {
    title: 'Venue Consulting',
    description:
      'Fractional operations leadership and interim consulting for client-based businesses and venues scaling live experiences or day-to-day operations.',
    canonicalPath: '/consulting',
  },
  '/software': {
    title: 'The Kewl Platform',
    description:
      'The software platform behind Kewl\'s operator team. BEOs, staffing, portals, ticketing, and financial visibility in one workspace \u2014 built by operators, in use in the field.',
    canonicalPath: '/software',
  },
  '/about': {
    title: 'About',
    description:
      'Operators first, software builders second. Meet the Phoenix-based team behind Kewl Event Solutions — three services, one team, one operating standard.',
    canonicalPath: '/about',
  },
  '/contact': {
    title: 'Contact',
    description:
      'Tell us what you need — event production, venue consulting, or the Kewl software platform. One form. We\'ll route it to the right team.',
    canonicalPath: '/contact',
  },
  '/signup': {
    title: 'Start Your Free Trial',
    description:
      'Start a free trial of the Kewl Platform — BEOs, staffing, client portals, ticketing, and financial visibility in one workspace built by event operators.',
    canonicalPath: '/signup',
  },
  '/login': {
    title: 'Log In',
    description: 'Log in to the Kewl Platform to manage your events, staff, portals, and finances.',
    canonicalPath: '/login',
  },
  '/terms': {
    title: 'Terms of Service',
    description: 'Kewl Event Solutions Terms of Service.',
    canonicalPath: '/terms',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'Kewl Event Solutions Privacy Policy.',
    canonicalPath: '/privacy',
  },
};

function RouteSEO({ pathname }) {
  // Blog pages own their SEO (per-post title, canonical, Article JSON-LD).
  if (pathname === '/blog' || pathname.startsWith('/blog/')) return null;
  const publicMeta = PUBLIC_ROUTE_META[pathname];
  if (publicMeta) {
    return <SEO {...publicMeta} />;
  }
  // Every non-public path (authenticated app, portals, payment flows,
  // magic-link redeems, ticket scans, invite acceptance, etc.) is noindexed.
  return <SEO noindex title="Private" />;
}

export default App;
